import React, { useCallback, useMemo } from 'react';
import { GroupingDataType, LeaderboardType, RankingDataType } from '../../types/leaderboard';
import { HIGHLIGHT_COUNT, HighlightsSlider } from '../HighlightsSlider/HighlightsSlider';
import { Box, styled } from '@mui/material';
import { useAppSelector } from '../../redux/hooks';
import { getEmailReportingListEventsDataSelector } from '../../redux/getEmailReportingListSilce';
import { getTrainingCompletionListEventsDataSelector } from '../../redux/getTrainingCompletionSilce';
import { getBadgeAchievementListEventsDataSelector } from '../../redux/getBadgeAchievementListSilce';
import { getCurrentCellStore } from '../../utils/util';
import { LEADERBOARD_TABS } from '../../constants/constants';

const StyledBox = styled(Box)(() => ({
  padding: '24px 0',
  marginBottom: '40px'
}));

const LeaderboardHighlights: React.FC<{ activeTab: LeaderboardType }> = ({ activeTab }) => {
  const userCurrentCellStore = getCurrentCellStore();

  const { dataEmailReporting } = useAppSelector(getEmailReportingListEventsDataSelector);
  const { dataTrainingCompletion } = useAppSelector(getTrainingCompletionListEventsDataSelector);
  const { dataBadgeAchievement } = useAppSelector(getBadgeAchievementListEventsDataSelector);

  const leaderboardDataByTab = useMemo(
    () => ({
      [LeaderboardType.EMAIL_REPORTING]: {
        rankings: dataEmailReporting.rankings.data,
        groups: dataEmailReporting.groups.data
      },
      [LeaderboardType.COURSE_COMPLETION]: {
        rankings: dataTrainingCompletion.rankings.data,
        groups: dataTrainingCompletion.groups.data
      },
      [LeaderboardType.ACHIEVEMENT_COMPLETION]: {
        rankings: dataBadgeAchievement.rankings.data,
        groups: dataBadgeAchievement.groups.data
      }
    }),
    [dataEmailReporting, dataTrainingCompletion, dataBadgeAchievement]
  );

  const isGroupingView = useMemo(() => {
    switch (activeTab) {
      case LeaderboardType.EMAIL_REPORTING:
        return !!userCurrentCellStore?.show_attribute_email_reporting;
      case LeaderboardType.COURSE_COMPLETION:
        return !!userCurrentCellStore?.show_attribute_training_completion;
      case LeaderboardType.ACHIEVEMENT_COMPLETION:
        return !!userCurrentCellStore?.show_attribute_achievement_completion;
      default:
        return false;
    }
  }, [
    activeTab,
    userCurrentCellStore?.show_attribute_email_reporting,
    userCurrentCellStore?.show_attribute_training_completion,
    userCurrentCellStore?.show_attribute_achievement_completion
  ]);

  const mapRankingsToHighlights = useCallback(
    (data: RankingDataType[]) => {
      if (!data) return [];
      return data
        .filter((item) => item.rank <= HIGHLIGHT_COUNT)
        .map((item, index) => ({
          id: `${activeTab}-${item.learner_id}-${index}`,
          rank: item.rank,
          name: `${item.first_name} ${item.last_name}`,
          points: item.total_points,
          point_type: 'Total Points',
          metric_type: LEADERBOARD_TABS[activeTab]
        }));
    },
    [activeTab]
  );

  const mapGroupsToHighlights = useCallback(
    (data: GroupingDataType[]) => {
      if (!data) return [];
      return data
        .filter((item) => item.rank <= HIGHLIGHT_COUNT)
        .map((item, index) => ({
          id: `${activeTab}-${item.group}-${index}`,
          rank: item.rank,
          name: item.group,
          points: item.average_points,
          point_type: 'Average Points',
          metric_type: LEADERBOARD_TABS[activeTab]
        }));
    },
    [activeTab]
  );

  const highlights = useMemo(() => {
    const config = leaderboardDataByTab[activeTab];
    if (!config) return [];
    return isGroupingView
      ? mapGroupsToHighlights(config.groups)
      : mapRankingsToHighlights(config.rankings);
  }, [
    activeTab,
    isGroupingView,
    mapGroupsToHighlights,
    mapRankingsToHighlights,
    leaderboardDataByTab
  ]);

  const fallbackHighlightItems = useMemo(
    () =>
      Array.from({ length: HIGHLIGHT_COUNT }, (_, index) => ({
        id: index + 1,
        rank: index + 1,
        name: 'Unawarded',
        points: 'N/A',
        point_type: isGroupingView ? 'Average Points' : 'Total Points',
        metric_type: LEADERBOARD_TABS[activeTab]
      })),
    [activeTab, isGroupingView]
  );

  return (
    <StyledBox>
      <HighlightsSlider
        activeTab={activeTab}
        fallbackHighlights={fallbackHighlightItems}
        highlights={highlights}
      />
    </StyledBox>
  );
};

export default LeaderboardHighlights;
